import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { RefreshCw, X, EyeOff, RotateCcw } from "lucide-react";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [processFor, setProcessFor] = useState(null);
  const [zohoConnected, setZohoConnected] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [b, o, s, z] = await Promise.all([
        api.get(`/zoho/bills?bill_status=${filter}&days=180`),
        api.get("/origins"),
        api.get("/suppliers"),
        api.get("/zoho/status"),
      ]);
      setBills(b.data); setOrigins(o.data); setSuppliers(s.data);
      setZohoConnected(!!z.data?.connected);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => bills.reduce((acc, b) => {
    acc[b.internal_status] = (acc[b.internal_status] || 0) + 1; return acc;
  }, {}), [bills]);

  const ignoreBill = async (id) => {
    if (!window.confirm("Mark this bill as ignored? You can undo later.")) return;
    await api.post(`/zoho/bills/${id}/ignore`); await load();
  };
  const resetBill = async (id) => {
    await api.post(`/zoho/bills/${id}/reset`); await load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (prev.size === bills.length) return new Set();
      return new Set(bills.map((b) => b.bill_id));
    });
  };
  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    if (!window.confirm(`${action === "ignore" ? "Ignore" : "Reset"} ${selected.size} bill${selected.size === 1 ? "" : "s"}?`)) return;
    await api.post("/zoho/bills/bulk", { bill_ids: [...selected], action });
    setSelected(new Set());
    await load();
  };

  if (!zohoConnected) {
    return (
      <div data-testid="bills-page">
        <PageHeader eyebrow="Bookkeeping" title="Zoho Bills Inbox"
          description="Pull supplier bills from Zoho Books and process them into Green Inventory." />
        <div className="bg-[hsl(31,65%,53%)]/10 border border-[hsl(31,65%,53%)]/30 rounded p-6">
          <p className="font-semibold text-[hsl(31,65%,53%)] mb-1">Zoho Books not connected</p>
          <p className="text-sm">Go to <a href="/settings" className="text-primary underline">Settings → Zoho Books → Connect</a> first.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="bills-page">
      <PageHeader
        eyebrow="Bookkeeping"
        title="Zoho Bills Inbox"
        description="Bills pulled from Zoho Books in the last 180 days. Process supplier bills for green coffee → creates a FIFO inventory layer; ignore non-coffee bills."
        actions={
          <button onClick={load} disabled={busy} data-testid="bills-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 items-center mb-4" data-testid="bills-filter">
        {[
          ["pending", "Pending", counts.pending],
          ["processed", "Processed", counts.processed],
          ["ignored", "Ignored", counts.ignored],
          ["all", "All", bills.length],
        ].map(([k, label, n]) => (
          <button key={k}
            onClick={() => setFilter(k)}
            data-testid={`bills-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === k ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card text-muted-foreground border-border hover:bg-secondary"
            }`}>
            {label}{n != null ? ` · ${n}` : ""}
          </button>
        ))}
      </div>

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4">{error}</div>}

      {selected.size > 0 && (
        <div className="bg-primary/5 border border-primary/30 rounded p-3 text-sm mb-3 flex items-center gap-3" data-testid="bills-bulk-bar">
          <span className="font-semibold text-primary">{selected.size} selected</span>
          <button onClick={() => bulkAction("ignore")} data-testid="bulk-ignore"
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold">
            Ignore selected
          </button>
          <button onClick={() => bulkAction("reset")} data-testid="bulk-reset"
            className="px-3 py-1.5 border border-border rounded text-xs hover:bg-secondary">
            Reset to pending
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-muted-foreground hover:underline">Clear</button>
        </div>
      )}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr>
              <th className="p-3 w-8">
                <input type="checkbox" data-testid="bills-select-all"
                  checked={bills.length > 0 && selected.size === bills.length}
                  onChange={toggleSelectAll}
                  className="accent-[hsl(20,39%,39%)]" />
              </th>
              <th className="text-left p-3 label-tiny">Date</th>
              <th className="text-left p-3 label-tiny">Vendor</th>
              <th className="text-left p-3 label-tiny">Bill #</th>
              <th className="text-right p-3 label-tiny">Total</th>
              <th className="text-left p-3 label-tiny">Status</th>
              <th className="text-right p-3 label-tiny">Action</th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">
                {busy ? "Loading…" : `No ${filter === "all" ? "" : filter + " "}bills.`}
              </td></tr>
            )}
            {bills.map((b) => (
              <tr key={b.bill_id} className="border-t border-border" data-testid={`bill-row-${b.bill_id}`}>
                <td className="p-3">
                  <input type="checkbox" data-testid={`select-${b.bill_id}`}
                    checked={selected.has(b.bill_id)}
                    onChange={() => toggleSelect(b.bill_id)}
                    className="accent-[hsl(20,39%,39%)]" />
                </td>
                <td className="p-3 font-mono text-xs">{b.date}</td>
                <td className="p-3 font-medium">
                  {b.vendor_name}
                  {b.matched_supplier && (
                    <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary font-bold">supplier</span>
                  )}
                  {b.matched_origin && !b.matched_supplier && (
                    <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,36%)] font-bold">origin</span>
                  )}
                </td>
                <td className="p-3 text-muted-foreground font-mono text-xs">{b.bill_number || "—"}</td>
                <td className="p-3 text-right font-mono">R {fmt(b.total)}</td>
                <td className="p-3">
                  <BillStatusBadge status={b.internal_status} zohoStatus={b.status} />
                </td>
                <td className="p-3 text-right">
                  {b.internal_status === "pending" && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => setProcessFor(b)} data-testid={`process-${b.bill_id}`}
                        className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold hover:bg-[hsl(20,41%,32%)]">
                        Process
                      </button>
                      <button onClick={() => ignoreBill(b.bill_id)} data-testid={`ignore-${b.bill_id}`}
                        title="Ignore (e.g. non-coffee bill)"
                        className="p-1.5 text-muted-foreground border border-border rounded hover:bg-secondary">
                        <EyeOff className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {(b.internal_status === "ignored" || b.internal_status === "processed") && (
                    <button onClick={() => resetBill(b.bill_id)} data-testid={`reset-${b.bill_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground border border-border rounded hover:bg-secondary">
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {processFor && (
        <ProcessModal
          bill={processFor}
          origins={origins}
          suppliers={suppliers}
          onClose={() => setProcessFor(null)}
          onProcessed={() => { setProcessFor(null); load(); }}
        />
      )}
    </div>
  );
}

function BillStatusBadge({ status, zohoStatus }) {
  const map = {
    pending:   { bg: "bg-secondary text-foreground", label: `Pending · ${zohoStatus || "?"}` },
    processed: { bg: "bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,36%)]", label: "Processed → inventory" },
    ignored:   { bg: "bg-muted text-muted-foreground", label: "Ignored" },
  };
  const x = map[status] || map.pending;
  return <span className={`px-2 py-1 rounded text-[10px] font-semibold ${x.bg}`}>{x.label}</span>;
}

function ProcessModal({ bill, origins, suppliers, onClose, onProcessed }) {
  const [form, setForm] = useState({
    origin_id: "", supplier_id: "",
    weight_kg: "", unit_cost_per_kg: "",
    purchase_date: bill.date,
    lot_ref: bill.bill_number || "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Try to suggest a supplier matching the vendor name
  useEffect(() => {
    if (form.supplier_id) return;
    const match = suppliers.find((s) => (s.name || "").toLowerCase().trim() === (bill.vendor_name || "").toLowerCase().trim());
    if (match) setForm((f) => ({ ...f, supplier_id: match.id }));
  }, [bill, suppliers, form.supplier_id]);

  const total = (parseFloat(form.weight_kg) || 0) * (parseFloat(form.unit_cost_per_kg) || 0);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api.post(`/zoho/bills/${bill.bill_id}/process`, {
        origin_id: form.origin_id,
        supplier_id: form.supplier_id || null,
        weight_kg: parseFloat(form.weight_kg),
        unit_cost_per_kg: parseFloat(form.unit_cost_per_kg),
        purchase_date: form.purchase_date,
        lot_ref: form.lot_ref,
        notes: form.notes,
      });
      onProcessed();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4" data-testid="process-bill-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="label-tiny">Process bill into inventory</p>
            <h3 className="font-display text-lg font-bold">{bill.vendor_name} · R {fmt(bill.total)}</h3>
            <p className="text-xs text-muted-foreground font-mono">{bill.bill_number} · {bill.date}</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 text-sm">
          <Field label="Origin *">
            <select required data-testid="bill-origin"
              value={form.origin_id} onChange={(e) => setForm({ ...form, origin_id: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded">
              <option value="">— choose —</option>
              {origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Supplier">
            <select data-testid="bill-supplier"
              value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded">
              <option value="">— none —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Weight kg *">
              <input type="number" step="0.01" required data-testid="bill-weight"
                value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-mono" />
            </Field>
            <Field label="Cost per kg (R) *">
              <input type="number" step="0.01" required data-testid="bill-cost"
                value={form.unit_cost_per_kg} onChange={(e) => setForm({ ...form, unit_cost_per_kg: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-mono" />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            Computed lot value: <span className="font-mono">R {fmt(total)}</span>
            {Math.abs(total - bill.total) > 0.01 && bill.total > 0 && (
              <span className="ml-2 text-[hsl(31,65%,53%)]">
                (bill total R {fmt(bill.total)} differs by R {fmt(total - bill.total)})
              </span>
            )}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Purchase date">
              <input type="date" value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-mono" />
            </Field>
            <Field label="Lot ref">
              <input type="text" value={form.lot_ref}
                onChange={(e) => setForm({ ...form, lot_ref: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} rows={2}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded" />
          </Field>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            <button type="submit" disabled={busy} data-testid="bill-process-submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
              {busy ? "Processing…" : "Process into inventory"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="label-tiny mb-1">{label}</p>
      {children}
    </div>
  );
}
